import React from "react";
import { Col, ConfigProvider, Form, Input, message, Modal, Row } from "antd";
import ClientService from "../../services/ClientService";
import { darkFormTheme, MODAL_STYLES, MODAL_TITLE_STYLE } from "../../../../styles/modalStyles";

const clientService = new ClientService();

interface CreateClientModalProps {
  visible: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const CreateClientModal: React.FC<CreateClientModalProps> = ({
  visible,
  onClose,
  onCreated,
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async () => {
    let values: {
      name: string;
      nuemonic: string;
      website?: string;
      primaryContact?: string;
      email?: string;
      phone?: string;
      address?: string;
      city?: string;
      state?: string;
      zipCode?: string;
      notes?: string;
    };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setIsSubmitting(true);
    try {
      await clientService.createClient({
        name: values.name.trim(),
        nuemonic: values.nuemonic.trim(),
      });
      message.success("Client created successfully");
      form.resetFields();
      onCreated();
    } catch {
      message.error("Failed to create client");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={<span style={MODAL_TITLE_STYLE}>Create New Client</span>}
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText="Create"
      okButtonProps={{ loading: isSubmitting }}
      destroyOnClose
      width={560}
      styles={MODAL_STYLES}
    >
      <ConfigProvider theme={darkFormTheme}>
      <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              name="name"
              label="Client Name"
              rules={[{ required: true, message: "Client Name is required" }]}
            >
              <Input placeholder="e.g. Westfield Capital Partners LP" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="nuemonic"
              label="Client Code"
              rules={[{ required: true, message: "Client Code is required" }]}
              extra="Short ID, e.g. WCP"
            >
              <Input placeholder="WCP" maxLength={20} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="website" label="Website">
          <Input placeholder="https://www.example.com" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="primaryContact"
              label="Primary Contact Name"
            >
              <Input placeholder="Jane Smith" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: "email", message: "Enter a valid email" }]}
            >
              <Input placeholder="jane@example.com" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="phone" label="Phone">
          <Input placeholder="(555) 000-0000" />
        </Form.Item>

        <Form.Item name="address" label="Street Address">
          <Input placeholder="1200 Innovation Way, Suite 400" />
        </Form.Item>

        <Row gutter={12}>
          <Col span={10}>
            <Form.Item name="city" label="City">
              <Input placeholder="San Francisco" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="state" label="State">
              <Input placeholder="CA" maxLength={2} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="zipCode" label="ZIP Code">
              <Input placeholder="94105" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={3} placeholder="Additional notes about this client..." />
        </Form.Item>
      </Form>
      </ConfigProvider>
    </Modal>
  );
};
